import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart controls whose click re-renders the line items and can detach the
 * pressed element from the DOM mid-click (see #installBackdropCloseGuard).
 */
const CART_CONTROL_SELECTOR = '.cart-items__remove, .quantity-minus, .quantity-plus';

/** Window (ms) after a cart-control press during which a drawer close() is treated as a light-dismiss misfire and swallowed. */
const REMOVE_GUARD_MS = 700;

/** Per-drawer state keys — Symbols so they never collide with a <theme-drawer> property. */
const CART_CONTROL_PRESS_AT = Symbol('cartControlPressAt');
const CART_CLOSE_GUARDED = Symbol('cartCloseGuarded');

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // Keep the drawer open when a line item is removed or its quantity changes.
    // Capture-phase so the press is timestamped before the click detaches the
    // control and Horizon's light-dismiss misreads it as an outside click.
    this.addEventListener('pointerdown', this.#handleControlPointerDown, true);
    this.#installBackdropCloseGuard();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('pointerdown', this.#handleControlPointerDown, true);
  }

  /**
   * Timestamps a press on a cart control that re-renders the line items
   * (remove / quantity ±). Runs in the capture phase so the mark is set before
   * the click is processed. The timestamp lives on the <theme-drawer> element
   * so it survives a Section Rendering morph that replaces this component.
   *
   * @param {PointerEvent} event
   */
  #handleControlPointerDown = (event) => {
    const drawer = this.#themeDrawer;
    if (!drawer || !(event.target instanceof Element)) return;
    if (event.target.closest(CART_CONTROL_SELECTOR)) {
      /** @type {any} */ (drawer)[CART_CONTROL_PRESS_AT] = performance.now();
    }
  };

  /**
   * Stops the cart drawer from light-dismissing itself when a line item is
   * removed. Horizon's `isClickedOutside()` (utilities.js) reads the click as
   * "outside" when the pressed control has already detached from the DOM
   * mid-click — `panel.contains(detachedTarget)` is false — and calls
   * `close()`. We wrap the drawer's public `close()` and swallow any close
   * firing within {@link REMOVE_GUARD_MS} of a cart-control press.
   *
   * Additive by design (no Horizon files touched) and fail-safe: if `close` is
   * ever renamed upstream the wrap simply doesn't install and the native close
   * keeps working. Idempotent — only the first instance wraps `close()`; the
   * press timestamp is shared via the drawer element so later instances (after
   * a morph) keep feeding the same guard.
   */
  #installBackdropCloseGuard() {
    const drawer = /** @type {any} */ (this.#themeDrawer);
    if (!drawer || typeof drawer.close !== 'function' || drawer[CART_CLOSE_GUARDED]) return;
    drawer[CART_CLOSE_GUARDED] = true;

    const nativeClose = drawer.close.bind(drawer);
    drawer.close = (...args) => {
      const pressedAt = drawer[CART_CONTROL_PRESS_AT];
      if (pressedAt != null && performance.now() - pressedAt < REMOVE_GUARD_MS) {
        return Promise.resolve();
      }
      return nativeClose(...args);
    };
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
